import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import api from '../services/api';

interface MergeTwoItemsProps {
  open: boolean;
  onClose: () => void;
  selectedItems: any[];
  onComplete: () => void;
}

interface MergeItem {
  store_id: number;
  store_name: string;
  manufacturer_id: number;
  manufacturer_name: string;
  medicine_name: string;
  pack_size: string;
  inventory_quantity: number;
}

const MergeTwoItems: React.FC<MergeTwoItemsProps> = ({ open, onClose, selectedItems, onComplete }: MergeTwoItemsProps) => {
  const [deleteItem, setDeleteItem] = useState<MergeItem | null>(null);
  const [mergeItem, setMergeItem] = useState<MergeItem | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open && selectedItems.length === 2) {
      // Reset state when dialog opens
      setDeleteItem(null);
      setMergeItem(null);
      setConfirmOpen(false);
      setError(null);
      setSuccess(false);
    }
  }, [open, selectedItems]);

  const assignToDelete = (item: any) => {
    setDeleteItem(item);
    // If this item was assigned to merge, clear it
    if (mergeItem?.manufacturer_id === item.manufacturer_id) {
      setMergeItem(null);
    }
  };

  const assignToMerge = (item: any) => {
    setMergeItem(item);
    // If this item was assigned to delete, clear it
    if (deleteItem?.manufacturer_id === item.manufacturer_id) {
      setDeleteItem(null);
    }
  };

  const handleConfirmMerge = () => {
    if (!deleteItem || !mergeItem) {
      setError('Please assign both items before merging');
      return;
    }
    setConfirmOpen(true);
  };

  const handleMerge = async () => {
    if (!deleteItem || !mergeItem) return;
    
    setError(null);
    setSaving(true);
    setConfirmOpen(false);

    try {
      const response = await api.post('/utilities/merge-items', {
        deleteItemId: deleteItem.manufacturer_id,
        mergeItemId: mergeItem.manufacturer_id,
        storeId: deleteItem.store_id
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onComplete();
          onClose();
        }, 2000);
      } else {
        setError('Failed to merge items');
      }
    } catch (err: any) {
      console.error('Error merging items:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to merge items');
    } finally {
      setSaving(false);
    }
  };

  if (selectedItems.length !== 2) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Merge Two Items</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            Exactly 2 items must be selected to perform a merge operation.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open && !confirmOpen} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Merge Two Items</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully merged items! Sales and purchase records have been transferred.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Assign one item to DELETE (will be removed) and the other to MERGE (will be kept with all combined data).
          </Typography>

          <Box sx={{ my: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Items:
            </Typography>
            {selectedItems.map((item: any, index: number) => (
              <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.medicine_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manufacturer: {item.manufacturer_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pack Size: {item.pack_size} | Stock: {item.inventory_quantity || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Store: {item.store_name} | ID: {item.manufacturer_id}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', minWidth: 150 }}>
                    <Button
                      variant={deleteItem?.manufacturer_id === item.manufacturer_id ? 'contained' : 'outlined'}
                      color="error"
                      size="small"
                      onClick={() => assignToDelete(item)}
                      fullWidth
                    >
                      Delete This
                    </Button>
                    <Button
                      variant={mergeItem?.manufacturer_id === item.manufacturer_id ? 'contained' : 'outlined'}
                      color="success"
                      size="small"
                      onClick={() => assignToMerge(item)}
                      fullWidth
                    >
                      Keep This
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Paper 
              sx={{ 
                flex: 1, 
                p: 2, 
                bgcolor: deleteItem ? 'error.light' : 'grey.100',
                border: '2px solid',
                borderColor: deleteItem ? 'error.main' : 'grey.300'
              }}
            >
              <Typography variant="subtitle2" color={deleteItem ? 'error.dark' : 'text.secondary'} gutterBottom>
                🗑️ DELETE ITEM
              </Typography>
              {deleteItem ? (
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {deleteItem.medicine_name}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {deleteItem.manufacturer_name}
                  </Typography>
                  <Typography variant="caption" color="error">
                    Will be permanently deleted
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No item assigned
                </Typography>
              )}
            </Paper>

            <Paper 
              sx={{ 
                flex: 1, 
                p: 2, 
                bgcolor: mergeItem ? 'success.light' : 'grey.100',
                border: '2px solid',
                borderColor: mergeItem ? 'success.main' : 'grey.300'
              }}
            >
              <Typography variant="subtitle2" color={mergeItem ? 'success.dark' : 'text.secondary'} gutterBottom>
                ✅ MERGE INTO (KEEP)
              </Typography>
              {mergeItem ? (
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {mergeItem.medicine_name}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {mergeItem.manufacturer_name}
                  </Typography>
                  <Typography variant="caption" color="success.dark">
                    Will receive all data
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No item assigned
                </Typography>
              )}
            </Paper>
          </Box>

          {deleteItem && mergeItem && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                This action cannot be undone!
              </Typography>
              <Typography variant="body2">
                All sales and purchase records from <strong>{deleteItem.medicine_name}</strong> will be transferred to <strong>{mergeItem.medicine_name}</strong>, and <strong>{deleteItem.medicine_name}</strong> will be permanently deleted from the system.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmMerge}
            disabled={!deleteItem || !mergeItem || saving}
            startIcon={saving && <CircularProgress size={20} />}
          >
            {saving ? 'Merging...' : 'Confirm Merge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm">
        <DialogTitle>Final Confirmation</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              WARNING: This action is permanent and cannot be undone!
            </Typography>
          </Alert>
          <Typography variant="body2" gutterBottom>
            You are about to:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body2">
                Transfer all sales records from <strong>{deleteItem?.medicine_name}</strong> to <strong>{mergeItem?.medicine_name}</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Transfer all purchase records from <strong>{deleteItem?.medicine_name}</strong> to <strong>{mergeItem?.medicine_name}</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Permanently delete <strong>{deleteItem?.medicine_name}</strong> (ID: {deleteItem?.manufacturer_id})
              </Typography>
            </li>
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Are you absolutely sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleMerge}
          >
            Yes, Merge Items
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MergeTwoItems;



